import React, { useState } from 'react';
import { CheckCircle2, Circle, FileText, Upload, User, Building2, Target, FolderOpen, ChevronRight } from 'lucide-react';
interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  category: string;
}
export function OnboardingPage() {
  const [checklist, setChecklist] = useState<ChecklistItem[]>([{
    id: '1',
    title: 'Complete Founder Profile',
    description: 'Personal details, background, and entrepreneurial journey',
    completed: true,
    category: 'profile'
  }, {
    id: '2',
    title: 'Upload Headshot & Bio',
    description: 'Professional photo and short bio for the cohort directory',
    completed: true,
    category: 'profile'
  }, {
    id: '3',
    title: 'Company Overview',
    description: 'Business name, industry, stage, and value proposition',
    completed: true,
    category: 'business'
  }, {
    id: '4',
    title: 'Revenue & Financial Snapshot',
    description: 'Current MRR, funding stage, and runway',
    completed: true,
    category: 'business'
  }, {
    id: '5',
    title: 'Team Composition',
    description: 'Current team size, key roles, and hiring plans',
    completed: false,
    category: 'business'
  }, {
    id: '6',
    title: 'Growth Challenges Assessment',
    description: 'Identify your top 3 bottlenecks to scaling',
    completed: false,
    category: 'goals'
  }, {
    id: '7',
    title: 'Six-Month Goals',
    description: 'Define measurable targets for the program duration',
    completed: false,
    category: 'goals'
  }, {
    id: '8',
    title: 'Advisory Preferences',
    description: 'Areas where you need the most strategic support',
    completed: false,
    category: 'goals'
  }, {
    id: '9',
    title: 'Upload Pitch Deck',
    description: 'Current investor deck (PDF or PPTX)',
    completed: false,
    category: 'documents'
  }, {
    id: '10',
    title: 'Upload Financial Projections',
    description: 'Revenue model or financial forecast spreadsheet',
    completed: false,
    category: 'documents'
  }, {
    id: '11',
    title: 'Upload Cap Table',
    description: 'Current ownership structure (if applicable)',
    completed: false,
    category: 'documents'
  }, {
    id: '12',
    title: 'Sign Program Agreement',
    description: 'Review and sign the accelerator participation agreement',
    completed: false,
    category: 'documents'
  }]);
  const toggleItem = (id: string) => {
    setChecklist(prev => prev.map(item => item.id === id ? {
      ...item,
      completed: !item.completed
    } : item));
  };
  const completedCount = checklist.filter(i => i.completed).length;
  const totalCount = checklist.length;
  const progressPercent = Math.round(completedCount / totalCount * 100);
  const categories = [{
    key: 'profile',
    label: 'Founder Profile',
    icon: User
  }, {
    key: 'business',
    label: 'Business Diagnostics',
    icon: Building2
  }, {
    key: 'goals',
    label: 'Growth Goals & Preferences',
    icon: Target
  }, {
    key: 'documents',
    label: 'Document Uploads',
    icon: FolderOpen
  }];
  return <div className="space-y-8 animate-in fade-in duration-500 max-w-4xl mx-auto" data-id="element-602">
      {/* Header */}
      <div className="border-b border-gray-200 pb-6" data-id="element-603">
        <h1 className="text-3xl font-heading font-bold text-catalyst-black tracking-tight" data-id="element-604">
          Onboarding Package
        </h1>
        <p className="text-gray-500 mt-2" data-id="element-605">
          Complete your needs assessment to help us tailor the accelerator
          experience to your business.
        </p>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8" data-id="element-606">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6" data-id="element-607">
          <div className="flex items-center gap-6" data-id="element-608">
            {/* Progress Ring */}
            <div className="relative w-20 h-20 shrink-0" data-id="element-609">
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80" data-id="element-610">
                <circle cx="40" cy="40" r="34" fill="none" stroke="#f3f4f6" strokeWidth="6" data-id="element-611" />
                <circle cx="40" cy="40" r="34" fill="none" stroke="#D4A843" strokeWidth="6" strokeLinecap="round" strokeDasharray={`${2 * Math.PI * 34}`} strokeDashoffset={`${2 * Math.PI * 34 * (1 - progressPercent / 100)}`} className="transition-all duration-700" data-id="element-612" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-613">
                <span className="text-lg font-bold text-catalyst-black" data-id="element-614">
                  {progressPercent}%
                </span>
              </div>
            </div>
            <div data-id="element-615">
              <h2 className="text-xl font-bold text-catalyst-black" data-id="element-616">
                Needs Assessment
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-617">
                {completedCount} of {totalCount} items completed
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm" data-id="element-618">
            <span className="flex items-center gap-1.5 text-green-600 font-medium" data-id="element-619">
              <CheckCircle2 size={16} data-id="element-620" /> {completedCount} Done
            </span>
            <span className="text-gray-300" data-id="element-621">|</span>
            <span className="flex items-center gap-1.5 text-gray-500 font-medium" data-id="element-622">
              <Circle size={16} data-id="element-623" /> {totalCount - completedCount} Remaining
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-6" data-id="element-624">
          <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-625">
            <div className="h-full bg-catalyst-mustard rounded-full transition-all duration-700" style={{
            width: `${progressPercent}%`
          }} data-id="element-626"></div>
          </div>
        </div>
      </div>

      {/* Checklist by Category */}
      <div className="space-y-6" data-id="element-627">
        {categories.map(cat => {
        const Icon = cat.icon;
        const items = checklist.filter(i => i.category === cat.key);
        const catCompleted = items.filter(i => i.completed).length;
        return <div key={cat.key} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-628">
              {/* Category Header */}
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between" data-id="element-629">
                <div className="flex items-center gap-3" data-id="element-630">
                  <div className="p-2 rounded-lg bg-catalyst-mustard/10" data-id="element-631">
                    <Icon size={20} className="text-catalyst-mustard-dark" data-id="element-632" />
                  </div>
                  <div data-id="element-633">
                    <h3 className="font-bold text-catalyst-black" data-id="element-634">
                      {cat.label}
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-635">
                      {catCompleted}/{items.length} completed
                    </p>
                  </div>
                </div>
                {catCompleted === items.length && <span className="text-xs font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full border border-green-100" data-id="element-636">
                    Complete
                  </span>}
              </div>

              {/* Items */}
              <div className="divide-y divide-gray-50" data-id="element-637">
                {items.map(item => <div key={item.id} onClick={() => toggleItem(item.id)} className="flex items-center gap-4 px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors group" data-id="element-638">
                    <button className="shrink-0" data-id="element-639">
                      {item.completed ? <CheckCircle2 size={22} className="text-catalyst-mustard" data-id="element-640" /> : <Circle size={22} className="text-gray-300 group-hover:text-catalyst-mustard/50 transition-colors" data-id="element-641" />}
                    </button>
                    <div className="flex-1 min-w-0" data-id="element-642">
                      <h4 className={`font-semibold text-sm ${item.completed ? 'text-gray-400 line-through decoration-gray-300' : 'text-catalyst-black'}`} data-id="element-643">
                        {item.title}
                      </h4>
                      <p className={`text-xs mt-0.5 ${item.completed ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-644">
                        {item.description}
                      </p>
                    </div>
                    {!item.completed && <ChevronRight size={18} className="text-gray-300 group-hover:text-catalyst-mustard transition-colors shrink-0" data-id="element-645" />}
                  </div>)}
              </div>
            </div>;
      })}
      </div>
    </div>;
}